import React, { useEffect, useState } from "react";

import ReactPlaceholder from "react-placeholder";
import "react-placeholder/lib/reactPlaceholder.css";
import { useAPI } from "../../../../../lib/common";
import Container from "@material-ui/core/Container";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { Link } from "react-router-dom";
import { generatePath, useHistory } from "react-router";
import useRoutes from "../../../../hooks/useRoutes";
import { usePictureSets } from "../../../../hooks/usePictureSets";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import { Trans, useTranslation } from "react-i18next";
import { Skeleton } from "@material-ui/lab";
import Box from "@material-ui/core/Box";
import { clientIsDesktop } from "../../../../../utils";

const useStyles = makeStyles((theme) => {
  return {
    categoryList: {
      margin: 0,
      padding: 0,
      listStyle: "none",
      display: "grid",
      marginBottom: theme.spacing(2),
      [theme.breakpoints.up("xs")]: {
        gridTemplateColumns: "repeat(1, 1fr)",
      },
      [theme.breakpoints.up("sm")]: {
        gridTemplateColumns: "repeat(2, 1fr)",
      },
      [theme.breakpoints.up("md")]: {
        gridTemplateColumns: "repeat(3, 1fr)",
      },

      gridGap: theme.spacing(2),
      "& > li": {
        paddingBottom: "100%",
        position: "relative",
        [theme.breakpoints.down("xs")]: {
          flexBasis: "50%",
        },
        backgroundColor: "#fefefe",
      },
    },
    container: {
      position: "absolute",
      left: 0,
      top: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "white",
      color: "black",
      textDecoration: "none",
      display: "flex",
      flexDirection: "column",
      boxShadow: "0px 3px 6px #00000029",
      "&:hover img, &:focus img": {
        transform: "scale(1.03)",
      },
      "&:hover": {
        textDecoration: "none",
      },
    },
    imageContainer: {
      paddingTop: "67%",
      position: "relative",
      flexShrink: 0,
      overflow: "hidden",
      "& > img, & > span": {
        transition: "transform 0.2s",
        position: "absolute",
        left: 0,
        top: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover",
      },
    },
    description: {
      padding: "4px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "start",
      alignItems: "center",
      marginTop: "5px",
      flexGrow: 1,
      "& span": {
        width: "200px",
      },
      "& h2": {
        textTransform: "uppercase",
        margin: 0,
        textAlign: "center",
        color: theme.palette.primary.main,
        fontSize: "20px",
        [theme.breakpoints.up("lg")]: {
          fontSize: "18px",
        },
      },
      "& p": {
        margin: 0,
        textAlign: "center",
        color: "black",
        fontSize: "15px",
        [theme.breakpoints.up("lg")]: {
          fontSize: "14px",
        },
      },
    },
  };
});

const getCategoryLink = (category, routes) => {
  if (!routes["products.photogifts.category"]) {
    console.log("Unable to find category in routes", category, routes);
  }
  let link = generatePath(routes["products.photogifts.category"].path, {
    type: "photogifts",
    category: category.internalName,
  });

  // Special cases:
  // FIXME: Order catalogues:
  if (category.internalName == "digitalprint") {
    link = generatePath(routes["products.digitalportraits"].path);
  } else if (category.internalName == "catalogues") {
    link = generatePath(routes["products.catalogues"].path);
  } else if (category.internalName == "cards") {
    if (category.subCategories.length > 0) {
      let subCategory = category.subCategories.find(
        (sc) => sc.default === true,
      );
      if (!subCategory) {
        subCategory = category.subCategories[0];
      }
      link = generatePath(routes["products.cards.category"].path, {
        type: "cards",
        category: subCategory.internalName,
      });
    } else {
      link = generatePath(routes["products.cards"].path, {
        type: "cards",
      });
    }
  } else if (category.internalName == "calendar") {
    link = generatePath(routes["products.calendars"].path, {
      type: "calendars",
    });
  } else if (category.internalName == "packages") {
    link = generatePath(routes["products.packages"].path);
  }
  return link;
};

export default () => {
  const { i18n } = useTranslation();
  const [data, loading, error] = useAPI(
    "/ajax/template/get_categories.php?lang=" + i18n.language,
  );
  const classes = useStyles();
  const routes = useRoutes();
  const { currentPictureSet } = usePictureSets();
  const [confirmSubjectChangeDialog, setConfirmSubjectChangeDialog] =
    useState();

  const checkForSubjectChange = (e, category) => {
    if (!category.firstQualifiedSubject) {
      return;
    }

    // No need for warning when we don't change subject:
    if (
      category.firstQualifiedSubject.uuid == currentPictureSet.subject.uuid
    ) {
      return;
    }

    e.preventDefault();
    setConfirmSubjectChangeDialog(category);
  };

  return (
    <Container>
      <ReactPlaceholder
        ready={!loading}
        customPlaceholder={<Placeholder />}
        showLoadingAnimation
      >
        <ul className={classes.categoryList}>
          {!loading &&
            data.categories
              .filter(
                (category) =>
                  ["archive"].indexOf(category.internalName.toLowerCase()) < 0,
              )
              .map((category, index) => {
                const link = getCategoryLink(category, routes);

                const LinkComponent = clientIsDesktop() ? "a" : Link;
                const linkProps = clientIsDesktop()
                  ? { href: link }
                  : { to: link };
                return (
                  <li key={category.category}>
                    <LinkComponent
                      className={classes.container}
                      {...linkProps}
                      onClick={(e) => checkForSubjectChange(e, category)}
                    >
                      <div className={classes.imageContainer}>
                        <img
                          src={category.image}
                          aria-labelledby={"category_" + category.internalName}
                        />
                      </div>
                      <div className={classes.description}>
                        <h2 id={"category_" + category.internalName}>
                          {category.title}
                        </h2>
                        <p>{category.description}</p>
                      </div>
                    </LinkComponent>
                  </li>
                );
              })}
        </ul>
      </ReactPlaceholder>
      {confirmSubjectChangeDialog && (
        <ChangeSubjectDialog
          category={confirmSubjectChangeDialog}
          onClose={() => setConfirmSubjectChangeDialog(false)}
        />
      )}
    </Container>
  );
};

const ChangeSubjectDialog = ({ category, onClose }) => {
  const { currentPictureSet, setCurrentPictureSet, pictureSets } =
    usePictureSets();
  const routes = useRoutes();
  const { t } = useTranslation();
  const history = useHistory();

  useEffect(() => {
    if (
      currentPictureSet.subject.uuid == category.firstQualifiedSubject.uuid
    ) {
      history.push(getCategoryLink(category, routes));
    }
  }, [currentPictureSet]);

  const changeSubject = () => {
    const pictureSet = pictureSets.find(
      (ps) => ps.subject.uuid == category.firstQualifiedSubject.uuid,
    );
    setCurrentPictureSet(pictureSet);
  };

  const newSubject = category.firstQualifiedSubject;
  const currentSubject = currentPictureSet.subject;

  return (
    <Dialog open={true} onClose={onClose}>
      <DialogTitle>
        <Trans>Change picture set</Trans>
      </DialogTitle>
      <DialogContent>
        <p>
          {t(
            "The selected picture set %s does not have pictures relevant for this category.",
            {
              postProcess: "sprintf",
              sprintf: [currentSubject.name + " -" + currentSubject.group],
            },
          )}
        </p>
        <p>
          {t(
            "We will therefore change to the most recent picture set with relevant pictures %s",
            {
              postProcess: "sprintf",
              sprintf: [newSubject.name + " -" + newSubject.group],
            },
          )}
        </p>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          <Trans>Cancel</Trans>
        </Button>
        <Button variant="contained" color="primary" onClick={changeSubject}>
          <Trans>Ok</Trans>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Placeholder = () => {
  const classes = useStyles();
  const items = [...Array(5).keys()];
  console.log("Items", items);

  return (
    <Box>
      <ul className={classes.categoryList}>
        {items.map((value, idx) => {
          return (
            <li key={idx}>
              <div className={classes.container}>
                <div className={classes.imageContainer}>
                  <Skeleton variant="rect" animation="pulse" />
                </div>
                <div className={classes.description}>
                  <Skeleton variant="text" animation="pulse" />
                  <Skeleton variant="text" animation="pulse" />
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </Box>
  );
};
